import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as ModelSkippedIcon } from '@elementary/common-ui/assets/16x16/model-skipped.svg';
import { ReactComponent as ModelSuccessIcon } from '@elementary/common-ui/assets/16x16/model-success-color.svg';
import { ReactComponent as ModelErrorIcon } from '@elementary/common-ui/assets/16x16/model-error-color.svg';
import { ReactComponent as TestRunFailed } from '@elementary/common-ui/assets/16x16/test-run-error-color.svg';
import { ReactComponent as TestRunWarning } from '@elementary/common-ui/assets/16x16/test-run-warning-color.svg';
import { ReactComponent as TestRunSuccess } from '@elementary/common-ui/assets/16x16/test-run-success-color.svg';
import { ReactComponent as TestRunSkipped } from '@elementary/common-ui/assets/16x16/test-run-skipped-color.svg';
import { ReactComponent as TableIcon } from '@elementary/common-ui/assets/16x16/table-update.svg';
import { ReactComponent as CriticalAssetTagColor } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import { ReactComponent as CriticalAssetTagUnmarkColor } from '@elementary/common-ui/assets/16x16/critical-asset-tag-unmark.svg';
import {
  AvatarGroup,
  Chip,
  EditableChip,
  Flex,
  FlexColumn,
  FlexCenter,
  Markdown,
  OverflowTooltip,
} from '@elementary/common-ui/components';
import useIsOverflow from '@elementary/common-ui/hooks/useIsOverflow';
import {
  formatTimeDuration,
  localizeDate,
} from '@elementary/common-ui/utils/datetime';
import { Tooltip, Typography } from '@mui/material';
import { AssetData, TestData } from 'shared/hooks/useAssetData/types';
import { pluralize } from 'humanize-plus';
import CopyButton from 'shared/components/CopyButton';
import ShowMore from '../../../catalog/components/ShowMore';
import TableUsagePopularity from '../TableUsage/TableUsagePopularity';
import useIsTableUsageSupported from '../../hooks/useIsTableUsageSupported';
import QualityDimensionChips from './QualityDimensionChips';
import useAssetDetailsSectionData from './hooks/useAssetDetailsSectionData';
import useCriticalAsset from './hooks/useCriticalAsset';

const DescriptionBox = styled(FlexColumn)<{ $isExpanded: boolean }>`
  overflow: hidden;
  max-height: ${({ $isExpanded }) => ($isExpanded ? undefined : '190px')};
`;

const SectionsGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  row-gap: 16px;
  column-gap: 24px;
  margin-top: 24px;
`;

const Section = styled.div`
  display: contents;
`;

const SectionTitle = styled(Typography)`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.common.gray2};
  margin-top: 4px;
  white-space: nowrap;
`;

const SectionContent = styled(Flex)`
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const SectionContentRow = styled(Flex)`
  gap: 8px;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const InfoItem = styled(FlexCenter)`
  gap: 6px;
  padding-inline: 6px;
`;

const InfoText = styled(Typography)`
  font-size: 14px;
  white-space: nowrap;
`;

const GrayText = styled(OverflowTooltip).attrs(({ theme }) => ({
  typographySx: { color: theme.palette.common.gray2 },
}))`
  flex-shrink: 1;
  min-width: 0;
  font-size: 14px;
`;

const EmptyStateText = styled(GrayText).attrs(({ theme }) => ({
  typographySx: { color: theme.palette.common.gray4 },
}))``;

const WrappingGrayText = styled(GrayText)`
  .MuiTypography-noWrap {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
    word-break: break-word;
  }
`;

const JsonPreview = styled.pre`
  margin: 0;
  font-family: inherit;
  white-space: pre-wrap;
  word-break: break-word;
`;

const NoPointerEvents = styled.div`
  pointer-events: none;
  display: flex;
  gap: 6px;
  align-items: center;
`;

const getRunStatusIcon = (status: string, isTest: boolean) => {
  switch (status) {
    case 'pass':
    case 'success':
      return isTest ? <TestRunSuccess /> : <ModelSuccessIcon />;
    case 'fail':
    case 'error':
      return isTest ? <TestRunFailed /> : <ModelErrorIcon />;
    case 'warn':
      return <TestRunWarning />;
    case 'skipped':
      return isTest ? <TestRunSkipped /> : <ModelSkippedIcon />;
    default:
      return isTest ? <TestRunSkipped /> : <ModelSkippedIcon />;
  }
};

const UsersProperty = ({
  users,
  title,
  withLabel,
}: {
  users: string[];
  title: string;
  withLabel?: boolean;
}) =>
  users.length === 0 ? null : (
    <Tooltip title={`${title}: ${users.join(', ')}`}>
      <Flex sx={{ gap: '6px', alignItems: 'center', paddingInline: '6px' }}>
        {withLabel && (
          <Typography sx={{ fontSize: '14px' }}>{title}</Typography>
        )}
        <NoPointerEvents>
          <AvatarGroup items={users} size={'small'} />
        </NoPointerEvents>
      </Flex>
    </Tooltip>
  );

const CRITICAL_OPTIONS = [
  { value: 'critical', label: 'Critical', icon: <CriticalAssetTagColor /> },
  {
    value: 'not_critical',
    label: 'Not critical',
    icon: <CriticalAssetTagUnmarkColor />,
  },
];

type Props = {
  asset?: AssetData;
  testData?: TestData;
  lastDataModificationTime?: string;
};

const DetailsSection = ({
  asset,
  testData,
  lastDataModificationTime,
}: Props) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const descriptionRef = useRef(null);
  const isDescriptionOverflow = useIsOverflow(descriptionRef, false, [
    asset,
    testData,
    isDescriptionExpanded,
  ]);

  const {
    isTest,
    assetId,
    isCriticalAssetsIds,
    description,
    status,
    latestExecution,
    shouldShowLastRun,
    path,
    ymlPath,
    subscribers,
    showSummaryButtons,
    testExecutionsByQualityDimension,
    owners,
    metaFields,
  } = useAssetDetailsSectionData({ asset, testData });

  useEffect(() => {
    setIsDescriptionExpanded(false);
  }, [asset, testData]);

  const {
    internalIsCritical,
    handleCriticalChange,
    isCriticalSaving,
    isCriticalAssetsLoading,
    hasSetCriticalAssetsPermissions,
  } = useCriticalAsset(isCriticalAssetsIds);

  const isTableUsageSupported = useIsTableUsageSupported();

  return (
    <FlexColumn>
      {description && (
        <FlexColumn sx={{ gap: 0.5 }}>
          <DescriptionBox
            ref={descriptionRef}
            $isExpanded={isDescriptionExpanded}
          >
            <Markdown>{description}</Markdown>
          </DescriptionBox>
          {(isDescriptionOverflow || isDescriptionExpanded) && (
            <ShowMore
              isExpanded={isDescriptionExpanded}
              onClick={() => setIsDescriptionExpanded((prev) => !prev)}
            />
          )}
        </FlexColumn>
      )}

      <SectionsGrid>
        {showSummaryButtons && (
          <Section>
            <SectionTitle>Tests</SectionTitle>
            <SectionContent>
              <QualityDimensionChips
                assetId={assetId}
                testExecutionsByQualityDimension={
                  testExecutionsByQualityDimension
                }
              />
            </SectionContent>
          </Section>
        )}

        <Section>
          <SectionTitle>Properties</SectionTitle>
          <SectionContent>
            {!testData && (
              <EditableChip
                value={internalIsCritical ? 'critical' : 'not_critical'}
                options={CRITICAL_OPTIONS}
                onSave={handleCriticalChange}
                disabled={!hasSetCriticalAssetsPermissions}
                isLoading={isCriticalSaving || isCriticalAssetsLoading}
              />
            )}

            <UsersProperty
              users={owners}
              title={pluralize(owners.length, 'Owner', 'Owners')}
              withLabel
            />

            <UsersProperty
              users={subscribers}
              title={pluralize(subscribers.length, 'Subscriber', 'Subscribers')}
            />

            {!!shouldShowLastRun && (
              <Tooltip
                title={
                  <FlexColumn>
                    <Typography
                      sx={{ color: 'common.white', fontSize: '12px' }}
                    >
                      {`Last run: ${localizeDate(latestExecution).format(
                        'L LT',
                      )}`}
                    </Typography>
                    <Typography
                      sx={{ color: 'common.white', fontSize: '12px' }}
                    >
                      Last run status: {status || 'N/A'}
                    </Typography>
                  </FlexColumn>
                }
              >
                <InfoItem>
                  {getRunStatusIcon(status, isTest)}

                  <InfoText>
                    {latestExecution
                      ? `${formatTimeDuration(latestExecution)} ago`
                      : '-'}
                  </InfoText>
                </InfoItem>
              </Tooltip>
            )}

            {!!lastDataModificationTime && (
              <Tooltip
                title={`Last table update: ${localizeDate(
                  lastDataModificationTime,
                ).format('L LT')}`}
              >
                <InfoItem>
                  <TableIcon />
                  <InfoText>
                    {lastDataModificationTime
                      ? `${formatTimeDuration(lastDataModificationTime)} ago`
                      : '-'}
                  </InfoText>
                </InfoItem>
              </Tooltip>
            )}

            {!testData &&
              isTableUsageSupported &&
              asset?.tableUsage !== undefined && (
                <InfoItem>
                  <TableUsagePopularity
                    usagePercentile={
                      asset.tableUsage ? asset.tableUsage.usagePercentile : null
                    }
                    periodStart={asset.tableUsage?.periodStart}
                    periodEnd={asset.tableUsage?.periodEnd}
                    queriesCount={asset.tableUsage?.queriesCount}
                    uniqueUserCount={asset.tableUsage?.uniqueUserCount}
                    barVariant="compact"
                  />
                </InfoItem>
              )}
          </SectionContent>
        </Section>

        <Section>
          <SectionTitle>Tags</SectionTitle>
          <SectionContent>
            {asset?.tags?.length ? (
              asset.tags.map((tag) => <Chip key={tag} label={`#${tag}`} />)
            ) : (
              <EmptyStateText>No tags</EmptyStateText>
            )}
          </SectionContent>
        </Section>

        {path && (
          <Section>
            <SectionTitle>Path</SectionTitle>
            <SectionContentRow>
              <GrayText>{path}</GrayText>
              <CopyButton textToCopy={path} />
            </SectionContentRow>
          </Section>
        )}

        {ymlPath && (
          <Section>
            <SectionTitle>YML</SectionTitle>
            <SectionContentRow>
              <GrayText>{ymlPath}</GrayText>
              <CopyButton textToCopy={ymlPath} />
            </SectionContentRow>
          </Section>
        )}

        {asset?.type === 'SEMANTIC' && asset?.platform && (
          <Section>
            <SectionTitle>Platform</SectionTitle>
            <SectionContent>
              <GrayText>{asset.platform}</GrayText>
            </SectionContent>
          </Section>
        )}

        {asset?.type === 'SEMANTIC' && (asset?.dbName || asset?.schemaName) && (
          <Section>
            <SectionTitle>Location</SectionTitle>
            <SectionContent>
              <GrayText>
                {[asset.dbName, asset.schemaName].filter(Boolean).join('.')}
              </GrayText>
            </SectionContent>
          </Section>
        )}

        {metaFields.map(({ title, value, type }) => (
          <Section key={title}>
            <SectionTitle>{title}</SectionTitle>
            <SectionContent>
              {type === 'list' ? (
                value.map((item: string) => <Chip key={item} label={item} />)
              ) : type === 'bool' ? (
                <WrappingGrayText>
                  {value?.toString().toUpperCase()}
                </WrappingGrayText>
              ) : type === 'object' ? (
                <Tooltip
                  title={
                    <JsonPreview>{JSON.stringify(value, null, 2)}</JsonPreview>
                  }
                >
                  <WrappingGrayText>{JSON.stringify(value)}</WrappingGrayText>
                </Tooltip>
              ) : (
                <WrappingGrayText>{String(value)}</WrappingGrayText>
              )}
            </SectionContent>
          </Section>
        ))}
      </SectionsGrid>
    </FlexColumn>
  );
};

export default DetailsSection;
